/* cart fix-1786551101 */
/**
 * Horizon cart bridge for custom Cart API adds (FBT, upsell, etc.).
 * Classic script (no ES imports): theme import maps are not always ready
 * when early module scripts run.
 *
 * Dispatches shopify:cart:lines-update with section HTML so cart-items
 * morphs empty → items and the drawer opens without a stale empty flash.
 *
 * Important: never fetch `pathname?sections=` with Accept: application/json
 * on product URLs. Shopify then returns product JSON instead of section HTML.
 * Always use the storefront root Section Rendering API for cart sections.
 *
 * Also syncs the header cart bubble imperatively so the top-right count
 * updates even if a listener misses the event promise.
 */
(function (window, document) {
  'use strict';

  /**
   * WebKit iOS: theme-drawer open() on an already-open dialog used to
   * panel.close()+showModal() and re-add --opening (left flicker + lost taps).
   * Patch prototype as soon as the custom element upgrades (belt + suspenders
   * if CDN asset lags).
   */
  function patchThemeDrawerOpenNoop() {
    try {
      var Ctor = customElements.get('theme-drawer');
      if (!Ctor || !Ctor.prototype || Ctor.prototype.__holsterOpenNoop) return;
      var proto = Ctor.prototype;
      var orig = proto.open;
      if (typeof orig !== 'function') return;
      proto.open = function (opts) {
        if (this.isOpen || this.hasAttribute('open')) return;
        return orig.call(this, opts);
      };
      proto.__holsterOpenNoop = true;
    } catch (e) {}
  }
  if (window.customElements) {
    customElements.whenDefined('theme-drawer').then(patchThemeDrawerOpenNoop);
    // In case already defined before bridge runs:
    patchThemeDrawerOpenNoop();
  }

  var EVENT_CART_LINES_UPDATE = 'shopify:cart:lines-update';
  var CART_DRAWER_SECTION = 'cart-drawer-section';
  var FETCH_TIMEOUT_MS = 6000;

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function withTimeout(promise, ms, label) {
    return new Promise(function (resolve, reject) {
      var done = false;
      var timer = window.setTimeout(function () {
        if (done) return;
        done = true;
        reject(new Error((label || 'fetch') + ' timeout'));
      }, ms);
      promise.then(
        function (value) {
          if (done) return;
          done = true;
          window.clearTimeout(timer);
          resolve(value);
        },
        function (err) {
          if (done) return;
          done = true;
          window.clearTimeout(timer);
          reject(err);
        }
      );
    });
  }

  /**
   * Immediately update header cart badge (top-right) without waiting for
   * cart-icon's event handler. Safe to call multiple times.
   */
  function syncCartBubble(itemCount) {
    var count = parseInt(itemCount, 10);
    if (isNaN(count) || count < 0) count = 0;

    document.querySelectorAll('cart-icon').forEach(function (icon) {
      var bubble =
        (icon.refs && icon.refs.cartBubble) || icon.querySelector('[ref="cartBubble"], .cart-bubble');
      var bubbleCount =
        (icon.refs && icon.refs.cartBubbleCount) ||
        icon.querySelector('[ref="cartBubbleCount"], .cart-bubble__text-count');
      if (!bubble || !bubbleCount) return;

      bubbleCount.textContent = count > 0 && count < 100 ? String(count) : count >= 100 ? '' : '0';
      bubbleCount.classList.toggle('hidden', count === 0);
      bubble.classList.toggle('visually-hidden', count === 0);
      icon.classList.toggle('header-actions__cart-icon--has-cart', count > 0);

      if (count > 0) {
        bubble.classList.add('cart-bubble--animating');
        window.setTimeout(function () {
          bubble.classList.remove('cart-bubble--animating');
        }, 600);
      }
    });

    try {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({ value: count < 100 ? String(count) : '', timestamp: Date.now() })
      );
    } catch (e) {}
  }

  function collectCartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      var id = el.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    if (ids.indexOf(CART_DRAWER_SECTION) === -1) {
      if (
        document.getElementById('shopify-section-' + CART_DRAWER_SECTION) ||
        document.getElementById('cart-drawer') ||
        document.querySelector('cart-items-component[data-drawer]')
      ) {
        ids.push(CART_DRAWER_SECTION);
      }
    }
    if (!ids.length) ids.push(CART_DRAWER_SECTION);
    return ids;
  }

  function toEventLines(items) {
    return (items || []).map(function (item) {
      return {
        merchandiseId: item.merchandiseId != null ? item.merchandiseId : item.id,
        quantity: item.quantity || 1,
      };
    });
  }

  function createCartFromAjaxResponse(ajaxCart) {
    if (!ajaxCart || typeof ajaxCart !== 'object') {
      return { id: null, totalQuantity: 0, lines: [] };
    }
    return {
      // Include token/id so Shopify event helpers that normalize cart GIDs don't throw.
      id: ajaxCart.token ? 'gid://shopify/Cart/' + ajaxCart.token : null,
      totalQuantity: ajaxCart.item_count || 0,
      note: ajaxCart.note || '',
      lines: (ajaxCart.items || []).map(function (item) {
        return {
          id: item.key,
          quantity: item.quantity,
          merchandiseId: item.variant_id,
        };
      }),
      cost: {
        totalAmount: { amount: String((ajaxCart.total_price || 0) / 100) },
      },
    };
  }

  function createDeferred() {
    var deferred = {};
    deferred.promise = new Promise(function (resolve, reject) {
      deferred.resolve = resolve;
      deferred.reject = reject;
    });
    return deferred;
  }

  function setDrawerRefreshing(on) {
    var inner = document.querySelector('#cart-drawer .cart-drawer__inner');
    if (!inner) return;
    inner.classList.toggle('cart-drawer--refreshing', !!on);
    if (on) inner.setAttribute('aria-busy', 'true');
    else inner.removeAttribute('aria-busy');
  }

  function openCartDrawer() {
    var themeDrawer = document.getElementById('cart-drawer');
    if (themeDrawer && typeof themeDrawer.open === 'function') {
      themeDrawer.open();
      return true;
    }
    return false;
  }

  function waitForCartDrawerFilled(timeoutMs) {
    return new Promise(function (resolve) {
      var start = performance.now();
      var tick = function () {
        // Scope to live drawer inner (not <template id="empty-cart-template">).
        var inner = document.querySelector('#cart-drawer .cart-drawer__inner');
        var empty = inner
          ? inner.querySelector('[data-cart-drawer-empty]') ||
            (inner.classList.contains('cart-drawer--empty') ? inner : null)
          : document.querySelector(
              '#cart-drawer [data-cart-drawer-empty], #cart-drawer .cart-drawer--empty'
            );
        var hasRows = inner
          ? inner.querySelector('[data-key], .cart-items__table-row, .cart-item')
          : document.querySelector(
              '#cart-drawer [data-key], #cart-drawer .cart-items__table-row, #cart-drawer .cart-item'
            );
        if ((!empty && hasRows) || performance.now() - start > timeoutMs) {
          resolve();
          return;
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  }

  function isSectionsPayload(sections, sectionIds) {
    if (!sections || typeof sections !== 'object') return false;
    // Product JSON from `/products/x?sections=` + Accept:json looks like { product: ... }
    if (sections.product && !sections[CART_DRAWER_SECTION] && !(sectionIds || []).some(function (id) {
      return typeof sections[id] === 'string' && sections[id].length > 50;
    })) {
      return false;
    }
    var ids = sectionIds && sectionIds.length ? sectionIds : Object.keys(sections);
    for (var i = 0; i < ids.length; i++) {
      var html = sections[ids[i]];
      if (typeof html === 'string' && html.indexOf('shopify-section') !== -1) return true;
      if (typeof html === 'string' && html.indexOf('cart-drawer') !== -1) return true;
      if (typeof html === 'string' && html.length > 200) return true;
    }
    return false;
  }

  /**
   * Dispatch Horizon cart lines update so cart-items / cart-drawer / cart-icon react.
   * Always bubble from body so cart-items-component does not ignore event.target === this.
   */
  function dispatchCartLinesUpdate(options) {
    var deferred = createDeferred();
    var target = document.body || document.documentElement;

    var event = new Event(EVENT_CART_LINES_UPDATE, { bubbles: true, cancelable: true });
    event.action = options.action || 'add';
    event.context = options.context || 'product';
    event.lines = options.lines || [];
    event.promise = deferred.promise;

    target.dispatchEvent(event);
    return deferred;
  }

  /**
   * Section Rendering API via storefront root.
   * Avoid product/collection JSON content negotiation traps.
   */
  function fetchSections(sectionIds) {
    if (!sectionIds || !sectionIds.length) return Promise.resolve({});
    var root = cartRoot();
    var url = root + (root.indexOf('?') === -1 ? '?' : '&') + 'sections=' + encodeURIComponent(sectionIds.join(','));
    return withTimeout(
      fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: '*/*' },
      })
        .then(function (r) {
          return r.ok ? r.json() : {};
        })
        .then(function (data) {
          if (isSectionsPayload(data, sectionIds)) return data;
          return {};
        }),
      FETCH_TIMEOUT_MS,
      'sections'
    ).catch(function () {
      return {};
    });
  }

  function fetchCart() {
    return withTimeout(
      fetch(cartRoot() + 'cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      }).then(function (r) {
        if (!r.ok) throw new Error('cart.js ' + r.status);
        return r.json();
      }),
      FETCH_TIMEOUT_MS,
      'cart.js'
    );
  }

  function resolveSections(providedSections, sectionIds) {
    if (isSectionsPayload(providedSections, sectionIds)) {
      return Promise.resolve(providedSections);
    }
    return fetchSections(sectionIds);
  }

  function emitLegacyCartEvents() {
    try {
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart-drawer:refreshed', { bubbles: true }));
    } catch (e) {}
  }

  /**
   * After a successful custom /cart/add.js, refresh Horizon cart UI.
   * @param {{ items?: Array, sections?: object|null, openDrawer?: boolean }} options
   */
  function notifyCartAdd(options) {
    options = options || {};
    var items = options.items || [];
    var providedSections = options.sections;
    var openDrawer = options.openDrawer !== false;
    var sectionIds = collectCartSectionIds();

    setDrawerRefreshing(true);
    var deferred = dispatchCartLinesUpdate({
      action: 'add',
      context: 'product',
      lines: toEventLines(items),
    });

    setDrawerRefreshing(true);
    var deferred = dispatchCartLinesUpdate({
      action: 'add',
      context: 'product',
      lines: toEventLines(items),
    });

    return Promise.all([fetchCart(), resolveSections(providedSections, sectionIds)])
      .then(function (results) {
        var cart = results[0];
        var sections = results[1] || {};
        var normalized = createCartFromAjaxResponse(cart);

        // Absolute count from cart.js (correct after 2nd ATC / FBT multi-add).
        syncCartBubble(normalized.totalQuantity);

        deferred.resolve({
          cart: normalized,
          detail: {
            sections: sections,
            items: cart.items,
            itemCount: cart.item_count,
            source: 'holster-cart-bridge',
            didError: false,
          },
        });

        emitLegacyCartEvents();

        if (!openDrawer) return cart;
        return waitForCartDrawerFilled(1600).then(function () {
          openCartDrawer();
          return cart;
        });
      })
      .catch(function (error) {
        // Still try emergency UI sync so badge/drawer recover without a header-cart click.
        try {
          if (window.HolsterCartRescue && typeof window.HolsterCartRescue.repair === 'function') {
            window.HolsterCartRescue.repair({ force: true, open: openDrawer });
          }
        } catch (rescueErr) {}
        deferred.reject(error);
        throw error;
      })
      .finally(function () {
        setDrawerRefreshing(false);
      });
  }

  /**
   * Add line items via Cart Ajax API and refresh Horizon UI.
   */
  function addItemsAndRefresh(items, options) {
    options = options || {};
    var sectionIds = collectCartSectionIds();
    var body = {
      items: items,
      sections: sectionIds.join(','),
      // Root URL keeps Section Rendering consistent across PDP/collection/search.
      sections_url: cartRoot(),
    };

    return fetch(cartRoot() + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    })
      .then(function (response) {
        return response
          .json()
          .catch(function () {
            return {};
          })
          .then(function (data) {
            if (!response.ok) {
              var err = new Error(data.description || data.message || 'Add to cart failed');
              err.payload = data;
              throw err;
            }
            return data;
          });
      })
      .then(function (data) {
        return notifyCartAdd({
          items: items,
          sections: isSectionsPayload(data.sections, sectionIds) ? data.sections : null,
          openDrawer: options.openDrawer !== false,
        }).then(function () {
          return data;
        });
      });
  }

  window.HolsterCart = {
    collectCartSectionIds: collectCartSectionIds,
    openCartDrawer: openCartDrawer,
    notifyCartAdd: notifyCartAdd,
    addItemsAndRefresh: addItemsAndRefresh,
    fetchSections: fetchSections,
    syncCartBubble: syncCartBubble,
  };

  // Permanent upsell binder (inline scripts in replaced drawer HTML do not re-execute)
  if (!window.__holsterCartUpsellBound) {
    window.__holsterCartUpsellBound = true;

    function upsellAdd(variantId, btn) {
      if (!variantId) return Promise.resolve();
      if (btn) btn.disabled = true;
      return addItemsAndRefresh([{ id: variantId, quantity: 1 }], { openDrawer: true })
        .catch(function () {
          return fetch(cartRoot() + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1 }],
              sections: 'cart-drawer-section',
              sections_url: cartRoot(),
            }),
          })
            .then(function (r) {
              return r.json().then(function (data) {
                if (!r.ok) throw new Error('upsell add failed');
                return data;
              });
            })
            .then(function (data) {
              return notifyCartAdd({
                items: [{ id: variantId, quantity: 1 }],
                sections: data.sections || null,
                openDrawer: true,
              });
            });
        })
        .then(function () {
          return fetch(cartRoot() + 'cart.js', { credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              if (typeof syncCartBubble === 'function') syncCartBubble(cart);
            });
        })
        .finally(function () {
          if (btn) btn.disabled = false;
        });
    }

    document.addEventListener(
      'click',
      function (ev) {
        var btn = ev.target && ev.target.closest && ev.target.closest('[data-holster-upsell-add]');
        if (!btn) return;
        ev.preventDefault();
        ev.stopPropagation();
        var form = btn.closest('form[data-holster-cart-add]');
        if (!form) return;
        var id = parseInt(String(new FormData(form).get('id') || ''), 10);
        upsellAdd(id, btn);
      },
      true
    );

    document.addEventListener(
      'submit',
      function (ev) {
        var form = ev.target;
        if (!(form instanceof HTMLFormElement) || !form.matches('[data-holster-cart-add]')) return;
        ev.preventDefault();
        ev.stopPropagation();
        var id = parseInt(String(new FormData(form).get('id') || ''), 10);
        var btn = form.querySelector('[data-holster-upsell-add], [type="submit"]');
        upsellAdd(id, btn);
      },
      true
    );
  }
})(window, document);
